import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Dropdown, Form, Header, Input, Message, Radio } from 'semantic-ui-react';

/**
 * Publish an existing post into another stream.
 *
 * Shown on both post detail screens — scheduled and sent — because "send this to another
 * audience too" is a decision made while looking at one specific post, and it is the one useful
 * thing left to do with a post that is past its edit window. The sent page used to say
 * *"This post can no longer be edited (it may be more than 2 days old)"* and stop there.
 *
 * **It copies, it does not move.** The original stays where it is, with its own comments, likes
 * and view counts; the copy gets its own. That is deliberate and is the reason this is a
 * duplication rather than a second reference — see `backend/.../utils/postDuplication.ts`.
 */

const STREAMS_I_CAN_POST_TO = gql`
  query StreamsICanPostTo {
    streamsICanPostTo { id name isDefault }
  }
`;

const PUBLISH_POST_TO_STREAM = gql`
  mutation PublishPostToStream($postId: Int!, $streamId: Int!, $publishAt: String) {
    publishPostToStream(postId: $postId, streamId: $streamId, publishAt: $publishAt) {
      # Post is a union; id must be selected per member type.
      ... on CommunityUpdate { id }
      ... on CommunityPrayerRequest { id }
      ... on LeadershipUpdate { id }
      ... on LeadershipPrayerRequest { id }
      ... on Birthday { id }
    }
  }
`;

/** A Date → what a `datetime-local` input needs, in the browser's own timezone. Mirrors the
 *  identical helper in PostCompose.tsx and PostScheduled.tsx. */
const toLocalInput = (date: Date) => {
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

/**
 * When the copy goes out.
 *
 * `SOURCE` only exists for a post that has not published yet — "the same time as this one" is
 * not a meaningful offer for something already sent, and the server would reject the past
 * timestamp anyway.
 */
type Timing = 'NOW' | 'SOURCE' | 'CUSTOM';

export const PublishToStream: React.FC<{
    postId: number;
    /** The stream the post is in now — never a destination. */
    currentStreamId?: number | null;
    /** Streams it has already been copied into, from `repostedStreamIds`. */
    repostedStreamIds?: number[];
    /** Whether the post is still waiting to go out, which changes what we can promise. */
    scheduled?: boolean;
    /** This post's own publish time, offered as "the same time as this post". */
    sourcePublishAt?: Date | null;
    onPublished?: () => void;
}> = ({ postId, currentStreamId, repostedStreamIds = [], scheduled = false, sourcePublishAt, onPublished }) => {
    const [streamId, setStreamId] = useState<number | null>(null);
    // A scheduled post defaults to going out alongside the original; a sent one to going now.
    const [timing, setTiming] = useState<Timing>(scheduled ? 'SOURCE' : 'NOW');
    const [customAt, setCustomAt] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<string | null>(null);

    const { data, loading } = useQuery(STREAMS_I_CAN_POST_TO);
    const [publish, { loading: publishing }] = useMutation(PUBLISH_POST_TO_STREAM);

    const all = data?.streamsICanPostTo ?? [];

    // Leave out the stream it is in and the ones it has already gone to, rather than letting
    // someone pick one and be told no — the server refuses both, but a picker that offers a
    // choice it will reject is just a slower error message.
    const taken = new Set<number>([
        ...(currentStreamId != null ? [currentStreamId] : []),
        ...repostedStreamIds,
    ]);

    const options = all
        .filter((s: any) => !taken.has(s.id))
        .map((s: any) => ({
            key: s.id,
            text: s.isDefault ? `${s.name} (default)` : s.name,
            value: s.id,
        }));

    const alreadyIn = all.filter((s: any) => repostedStreamIds.includes(s.id));

    if (loading) {
        return null;
    }

    // One stream in the organisation and the post is already in it: there is nowhere to send it,
    // and an empty picker would be a worse answer than no picker.
    if (options.length === 0 && alreadyIn.length === 0) {
        return null;
    }

    // `undefined` means "use the server's default", which is now for a sent post and the
    // original's own time for a scheduled one. Only a custom choice is sent explicitly, so
    // "now" never travels as a timestamp that could land a second in the past.
    const chosenPublishAt = (): string | undefined => {
        if (timing === 'CUSTOM' && customAt !== '') {
            return new Date(customAt).toISOString();
        }
        if (timing === 'SOURCE' && sourcePublishAt != null) {
            return sourcePublishAt.toISOString();
        }
        return undefined;
    };

    const describeWhen = (): string => {
        if (timing === 'CUSTOM' && customAt !== '') {
            return `It will publish on ${new Date(customAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.`;
        }
        if (timing === 'SOURCE') {
            return 'It will publish at the same time as this post.';
        }
        return 'It is live now.';
    };

    const customInPast = timing === 'CUSTOM'
        && customAt !== ''
        && new Date(customAt).getTime() < Date.now() - 60 * 1000;

    const handlePublish = async () => {
        if (streamId == null) return;
        setError(null);
        setDone(null);
        try {
            await publish({ variables: { postId, streamId, publishAt: chosenPublishAt() ?? null } });
            const name = all.find((s: any) => s.id === streamId)?.name ?? 'that stream';
            setDone(`Copied into ${name}. ${describeWhen()}`);
            setStreamId(null);
            onPublished?.();
        } catch (e: any) {
            setError(e?.message || 'Could not publish to that stream.');
        }
    };

    return (
        <>
            <Header as="h3" style={{ marginBottom: '0.3em' }}>Publish to another stream</Header>
            <p style={{ color: '#666', marginTop: 0 }}>
                Sends a copy to a second audience. The original stays where it is, and each copy
                keeps its own comments and likes.
            </p>

            {alreadyIn.length > 0 && (
                <Message
                    info
                    size="small"
                    content={`Already published to ${alreadyIn.map((s: any) => s.name).join(', ')}.`}
                />
            )}

            {error && <Message error size="small" content={error} />}
            {done && <Message positive size="small" content={done} />}

            {options.length > 0 ? (
                <>
                    <Form>
                        <Form.Field>
                            <label htmlFor="publish-to-stream-id">Stream</label>
                            <Dropdown
                                id="publish-to-stream-id"
                                selection
                                search
                                placeholder="Choose a stream"
                                options={options}
                                value={streamId ?? undefined}
                                disabled={publishing}
                                onChange={(_, d) => setStreamId(d.value as number)}
                                style={{ minWidth: '240px' }}
                            />
                        </Form.Field>

                        <Form.Field>
                            <label>When should it go out?</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                                {/* Only offered while the original is still waiting — lining the
                                    two up is meaningless once this one has already gone. */}
                                {scheduled && sourcePublishAt != null && (
                                    <Radio
                                        id="publish-timing-source"
                                        name="publishTiming"
                                        label={`At the same time as this post (${sourcePublishAt.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })})`}
                                        checked={timing === 'SOURCE'}
                                        disabled={publishing}
                                        onChange={() => setTiming('SOURCE')}
                                    />
                                )}
                                <Radio
                                    id="publish-timing-now"
                                    name="publishTiming"
                                    label="Now"
                                    checked={timing === 'NOW'}
                                    disabled={publishing}
                                    onChange={() => setTiming('NOW')}
                                />
                                <Radio
                                    id="publish-timing-custom"
                                    name="publishTiming"
                                    label="At a specific time"
                                    checked={timing === 'CUSTOM'}
                                    disabled={publishing}
                                    onChange={() => {
                                        setTiming('CUSTOM');
                                        // Seed from the original's time where there is one, so
                                        // the field opens somewhere sensible rather than blank.
                                        if (customAt === '') {
                                            setCustomAt(toLocalInput(
                                                sourcePublishAt != null && sourcePublishAt.getTime() > Date.now()
                                                    ? sourcePublishAt
                                                    : new Date(Date.now() + 60 * 60 * 1000)
                                            ));
                                        }
                                    }}
                                />
                            </div>
                        </Form.Field>

                        {timing === 'CUSTOM' && (
                            <Form.Field error={customInPast}>
                                {/* Explicit id/htmlFor: a bare <label> beside an <input> is not
                                    an association, so without it the field has no accessible
                                    name. */}
                                <label htmlFor="publish-to-stream-at">Publish date &amp; time</label>
                                <Input
                                    id="publish-to-stream-at"
                                    type="datetime-local"
                                    value={customAt}
                                    min={toLocalInput(new Date())}
                                    disabled={publishing}
                                    onChange={(_, { value }) => setCustomAt(value)}
                                />
                                {customInPast && (
                                    <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.4em' }}>
                                        That time has passed. Pick a time in the future, or choose "Now".
                                    </div>
                                )}
                            </Form.Field>
                        )}
                    </Form>

                    <Button
                        primary
                        icon="share square"
                        labelPosition="left"
                        content={timing === 'NOW' ? 'Publish to stream' : 'Schedule to stream'}
                        loading={publishing}
                        disabled={
                            publishing
                            || streamId == null
                            || customInPast
                            || (timing === 'CUSTOM' && customAt === '')
                        }
                        onClick={handlePublish}
                        style={{ marginTop: '1em' }}
                    />
                </>
            ) : (
                <p style={{ color: '#999' }}>
                    There are no other streams you can publish this into.
                </p>
            )}
        </>
    );
};
